import React from 'react';
import styled from 'styled-components';
import { CommonCss } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { FormArea } from 'components/forms';
import PhotoUploadField from './PhotoUploadField';

export const DetailSectionTitle = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  font-weight: 600;
  color: ${theme.colors.text.tertiary};
  margin: 6px 0 2px;
  padding-top: 6px;
  border-top: 1px solid ${theme.colors.border};

  &:first-child {
    margin-top: 0;
    padding-top: 0;
    border-top: none;
  }
`;

export const DetailFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 8px 10px 12px;
`;

export const DetailFormBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PersonSection = styled.div`
  flex-shrink: 0;
  padding-bottom: 10px;
  margin-bottom: 4px;
  border-bottom: 1px solid ${theme.colors.border};
`;

export const PersonHeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-shrink: 0;
  margin-top: 4px;
`;

export const PersonBesidePhoto = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  padding-top: 2px;
`;

export const PlaceholderWrap = styled.div`
  padding: 24px 16px;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.6;
`;

const GENDER_OPTIONS = [
  { value: 'M', label: '남' },
  { value: 'F', label: '여' },
  { value: 'U', label: '미상' },
];

const EMP_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: '재직' },
  { value: 'ON_LEAVE', label: '휴직' },
  { value: 'TERMINATED', label: '퇴직' },
];

const EMPLOYMENT_CLASS_OPTIONS = [
  { value: 'INSTITUTION_STAFF', label: '기관직원' },
  { value: 'CIVIL_SERVANT', label: '공무원' },
  { value: 'CONTRACT', label: '계약직' },
];

/** 필드명 컬럼 폭 통일 → 입력이 같은 세로선에서 시작 */
const LW = '56px';

export const BASIC1_PERSON_ROW1 = [
  [
    { name: 'empNo', label: '사번', type: 'text', width: CommonCss.w100, labelWidth: LW, required: true },
    { name: 'personNm', label: '성명', type: 'text', width: CommonCss.w100, labelWidth: LW, required: true },
  ],
];

export const BASIC1_PERSON_ROW2 = [
  [
    { name: 'birthDate', label: '생년월일', type: 'date', width: CommonCss.w110, labelWidth: LW },
    {
      name: 'genderCode',
      label: '성별',
      type: 'select',
      width: CommonCss.w80,
      labelWidth: LW,
      options: GENDER_OPTIONS,
    },
  ],
];

export const BASIC1_CONTACT_FIELDS = [
  [
    { name: 'mobileNo', label: '휴대전화', type: 'text', width: CommonCss.w110, labelWidth: LW },
    { name: 'email', label: '이메일', type: 'text', width: CommonCss.w150, labelWidth: LW },
  ],
];

export const BASIC1_EMPLOYMENT_FIELDS = [
  [
    {
      name: 'employmentClass',
      label: '고용신분',
      type: 'select',
      width: CommonCss.w100,
      labelWidth: LW,
      required: true,
      options: EMPLOYMENT_CLASS_OPTIONS,
    },
    {
      name: 'empStatus',
      label: '재직상태',
      type: 'select',
      width: CommonCss.w80,
      labelWidth: LW,
      options: EMP_STATUS_OPTIONS,
    },
    { name: 'firstAppointDate', label: '입사일', type: 'date', width: CommonCss.w100, labelWidth: LW },
    { name: 'terminatedDate', label: '퇴직일', type: 'date', width: CommonCss.w100, labelWidth: LW },
  ],
];

export const BASIC1_SNAPSHOT_FIELDS = [
  [
    { name: 'gradeNm', label: '직급', type: 'text', width: CommonCss.w80, labelWidth: LW, readonly: true },
    { name: 'orgUnitNm', label: '부서', type: 'text', width: CommonCss.w100, labelWidth: LW, readonly: true },
    { name: 'positionTitle', label: '직위', type: 'text', width: CommonCss.w80, labelWidth: LW, readonly: true },
    { name: 'payStep', label: '호봉', type: 'text', width: CommonCss.w50, labelWidth: LW, readonly: true },
  ],
  [
    {
      name: 'appointmentTypeNm',
      label: '발령유형',
      type: 'text',
      width: CommonCss.w120,
      labelWidth: LW,
      readonly: true,
    },
  ],
];

export const BASIC2_ADDRESS_FIELDS = [
  [
    { name: 'zipCode', label: '우편번호', type: 'text', width: CommonCss.w70, labelWidth: LW },
    { name: 'address', label: '주소', type: 'text', width: CommonCss.w180, labelWidth: LW, flex: '1 1 120px' },
    { name: 'addressDtl', label: '상세', type: 'text', width: CommonCss.w100, labelWidth: LW, flex: '1 1 80px' },
  ],
];

export const BASIC2_EXTRA_FIELDS = [
  [
    { name: 'terminatedReason', label: '퇴직사유', type: 'text', width: CommonCss.w180, labelWidth: LW, flex: '1 1 160px' },
    {
      name: 'lastEducationSummary',
      label: '최종학력',
      type: 'text',
      width: CommonCss.w180,
      labelWidth: LW,
      flex: '1 1 160px',
      readonly: true,
    },
  ],
  [
    {
      name: 'remark',
      label: '비고',
      type: 'textarea',
      width: '100%',
      labelWidth: LW,
      flex: '1 1 100%',
      rows: 3,
    },
  ],
];

export const EmployeeBasicInfo1Tab = ({ tenantCode }) => (
  <DetailFormWrap>
    <PersonSection>
      <DetailSectionTitle>인적사항</DetailSectionTitle>
      <PersonHeroRow>
        <PhotoUploadField tenantCode={tenantCode} />
        <PersonBesidePhoto>
          <FormArea fields={BASIC1_PERSON_ROW1} dense />
          <FormArea fields={BASIC1_PERSON_ROW2} dense />
        </PersonBesidePhoto>
      </PersonHeroRow>
    </PersonSection>
    <DetailFormBody>
      <DetailSectionTitle>연락처</DetailSectionTitle>
      <FormArea fields={BASIC1_CONTACT_FIELDS} dense />
      <DetailSectionTitle>재직정보</DetailSectionTitle>
      <FormArea fields={BASIC1_EMPLOYMENT_FIELDS} dense />
      <DetailSectionTitle>현재 발령 (기준일)</DetailSectionTitle>
      <FormArea fields={BASIC1_SNAPSHOT_FIELDS} dense />
    </DetailFormBody>
  </DetailFormWrap>
);

export const EmployeeBasicInfo2Tab = () => (
  <DetailFormWrap>
    <DetailFormBody>
      <DetailSectionTitle>주소</DetailSectionTitle>
      <FormArea fields={BASIC2_ADDRESS_FIELDS} dense />
      <DetailSectionTitle>부가정보</DetailSectionTitle>
      <FormArea fields={BASIC2_EXTRA_FIELDS} dense />
    </DetailFormBody>
  </DetailFormWrap>
);

export const EmployeeMasterPlaceholderTab = ({ tabLabel }) => (
  <PlaceholderWrap>
    <div style={{ fontWeight: 600, marginBottom: 6 }}>{tabLabel || '추가 영역'}</div>
    <div>이 영역은 더존 사원등록 레이아웃을 참고해 단계적으로 확장할 예정입니다.</div>
    <div style={{ marginTop: 8, color: theme.colors.text.tertiary, fontSize: theme.typography.fontSize.xs }}>
      기관별로 탭 표시 여부는 공통코드/환경설정으로 제어할 수 있습니다.
    </div>
  </PlaceholderWrap>
);
